import React, { useState, useEffect } from "react";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Button, Modal, ListGroup } from "react-bootstrap";
import "../../../global-styles.css";

const Products = () => {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsProduct, setDetailsProduct] = useState(null);
    const [newProduct, setNewProduct] = useState({
        categoryId: "",
        individualCode: "",
        name: "",
        price: "",
        quantity: "",
        description: "",
        liked: false,
        isDeleted: false,
        images: [],
        isHotProduct: false,
    });
    const [specificParams, setSpecificParams] = useState([{ key: "", value: "" }]);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const fetchProducts = async () => {
        try {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                Notify.failure(t("products.errors.tokenNotFound"));
                return;
            }

            const size = 200;
            let page = 0;
            let all = [];

            while (true) {
                const { data } = await axios.get(
                    `${apiBaseUrl}/public/all-products/get`,
                    {
                        params: { page, size, sortBy: "id", sortDir: "asc" },
                        headers: { Authorization: `Bearer ${accessToken}` },
                    }
                );

                const chunk = Array.isArray(data?.content)
                    ? data.content
                    : Array.isArray(data)
                        ? data
                        : [];

                all = all.concat(chunk);

                if (data?.last || chunk.length < size) break;

                page += 1;
            }

            setProducts(all);
        } catch (error) {
            console.error("Error fetching products:", error);
            Notify.failure(t("products.errors.fetchFailed"));
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${apiBaseUrl}/public/categories`);
            setCategories(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Error fetching categories:", error);
            Notify.failure(t('products.errors.categoriesFetchFailed'));
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const [imageUrls, setImageUrls] = useState({});
    useEffect(() => {
        const loadImages = async () => {
            const urls = {};
            await Promise.all(
                products.map(async (product) => {
                    if (product.imageUrls && product.imageUrls.length > 0) {
                        const url = await fetchImageWithAuth(product.imageUrls[0]);
                        urls[product.id] = url;
                    }
                })
            );
            setImageUrls(urls);
        };
        if (products.length > 0) {
            loadImages();
        }
    }, [products]);

    const fetchImageWithAuth = async (fileName) => {
        try {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                console.error(t('products.errors.tokenNotFound'));
                return "/placeholder.png";
            }
            const response = await fetch(
                `${apiBaseUrl}/public/images/${encodeURIComponent(fileName)}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (!response.ok) {
                console.error(t('products.errors.imageLoadError'), response.status);
                return "/placeholder.png";
            }
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error(t('products.errors.imageLoadError'), error);
            return "/placeholder.png";
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setNewProduct((prev) => ({
            ...prev,
            images: [...prev.images, ...files],
        }));
    };

    const handleSpecificParamChange = (index, field, value) => {
        const updatedParams = [...specificParams];
        updatedParams[index][field] = value;
        setSpecificParams(updatedParams);
    };

    const addSpecificParam = () => {
        setSpecificParams([...specificParams, { key: "", value: "" }]);
    };

    const removeSpecificParam = (index) => {
        const updatedParams = specificParams.filter((_, i) => i !== index);
        setSpecificParams(updatedParams);
    };

    const handleAddProduct = async (e) => {
        e.preventDefault();
        try {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                Notify.failure(t('products.errors.tokenNotFound'));
                return;
            }
            if (!newProduct.name || !newProduct.price || !newProduct.quantity) {
                Notify.failure(t('products.errors.fillRequired'));
                return;
            }
            const formData = new FormData();
            formData.append("name", newProduct.name);
            formData.append("price", newProduct.price);
            formData.append("quantity", newProduct.quantity);

            const specificParamsObject = {};
            specificParams.forEach((param) => {
                if (param.key && param.value) {
                    specificParamsObject[param.key] = param.value;
                }
            });
            formData.append("specificParams", JSON.stringify(specificParamsObject));

            newProduct.images.forEach((image) => {
                formData.append("images", image);
            });

            if (newProduct.categoryId) {
                formData.append("categoryId", newProduct.categoryId);
            }
            if (newProduct.individualCode) {
                formData.append("individualCode", newProduct.individualCode);
            }
            if (newProduct.description) {
                formData.append("description", newProduct.description);
            }
            formData.append("liked", newProduct.liked.toString());
            formData.append("isHotProduct", newProduct.isHotProduct.toString());

            const response = await axios.post(
                `${apiBaseUrl}/manager/catalogs/${newProduct.categoryId}/product-create`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setProducts([...products, response.data]);

            setNewProduct({
                categoryId: "",
                individualCode: "",
                name: "",
                price: "",
                quantity: "",
                description: "",
                liked: false,
                isDeleted: false,
                images: [],
                isHotProduct: false,
            });
            setSpecificParams([{ key: "", value: "" }]);

            Notify.success(t('products.success.added'));
        } catch (error) {
            console.error("Error adding product:", error);
            Notify.failure(t('products.errors.addFailed'));
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!productId) {
            Notify.failure(t('products.errors.invalidId'));
            return;
        }
        try {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                Notify.failure(t('products.errors.tokenNotFound'));
                return;
            }
            await axios.put(`${apiBaseUrl}/manager/delete-product/${productId}`, null, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            Notify.success(t('products.success.deleted'));
            await fetchProducts();
        } catch (error) {
            console.error("Error deleting product:", error.response?.data || error.message);
            Notify.failure(`${t('products.errors.deleteFailed')}: ${error.message}`);
        }
    };

    const renderSpecificParams = (params, limit) => {
        if (!params || typeof params !== "object") return <span>-</span>;
        const entries = Object.entries(params);
        if (entries.length === 0) return <span>-</span>;

        const limited = typeof limit === "number" ? entries.slice(0, limit) : entries;

        if (limit === 1 && limited.length === 1) {
            const [key, value] = limited[0];
            return <span><strong>{key}:</strong> {value}</span>;
        }

        return (
            <ul style={{ margin: 0, paddingLeft: "15px" }}>
                {limited.map(([key, value]) => (
                    <li key={key}>
                        <strong>{key}:</strong> {value}
                    </li>
                ))}
            </ul>
        );
    };

    const formatPriceKZT = (value) => {
        const num = Number(value);
        if (Number.isNaN(num)) return value;
        return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(num));
    };

    const handleEditClick = (product) => {
        const specificParamsArray = product.specificParams
            ? Object.entries(product.specificParams).map(([key, value]) => ({ key, value }))
            : [{ key: "", value: "" }];

        setEditingProduct({
            ...product,
            specificParams: specificParamsArray,
            images: [],
            imageUrls: product.imageUrls || [],
            deletedImageUrls: [],
        });
        setShowEditModal(true);
    };

    const handleEditSpecificParamChange = (index, field, value) => {
        const updatedParams = [...editingProduct.specificParams];
        updatedParams[index][field] = value;
        setEditingProduct({
            ...editingProduct,
            specificParams: updatedParams,
        });
    };

    const addEditSpecificParam = () => {
        setEditingProduct({
            ...editingProduct,
            specificParams: [...editingProduct.specificParams, { key: "", value: "" }],
        });
    };

    const removeEditSpecificParam = (index) => {
        const updatedParams = editingProduct.specificParams.filter((_, i) => i !== index);
        setEditingProduct({
            ...editingProduct,
            specificParams: updatedParams,
        });
    };

    const handleEditImageChange = (e) => {
        const files = Array.from(e.target.files);
        console.log("New images selected:", files);
        setEditingProduct((prev) => ({
            ...prev,
            images: [...(prev.images || []), ...files],
        }));
    };

    const handleDeleteCurrentImage = (imageUrl, indexToDelete) => {
        setEditingProduct((prev) => ({
            ...prev,
            imageUrls: prev.imageUrls.filter((_, index) => index !== indexToDelete),
            deletedImageUrls: [...prev.deletedImageUrls, imageUrl],
        }));
    };

    const handleDeleteNewImage = (indexToDelete) => {
        setEditingProduct((prev) => ({
            ...prev,
            images: prev.images.filter((_, index) => index !== indexToDelete),
        }));
    };

    const handleUpdateProduct = async (e) => {
        e.preventDefault();
        try {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                Notify.failure(t("products.errors.tokenNotFound"));
                return;
            }

            const formData = new FormData();
            formData.append("name", editingProduct.name);
            formData.append("price", editingProduct.price);
            formData.append("quantity", editingProduct.quantity);

            const specificParamsObject = {};
            (editingProduct?.specificParams || []).forEach((param) => {
                if (param.key && param.value) {
                    specificParamsObject[param.key] = param.value;
                }
            });
            formData.append("specificParams", JSON.stringify(specificParamsObject));

            if (Array.isArray(editingProduct.images) && editingProduct.images.length > 0) {
                editingProduct.images.forEach((image) => {
                    formData.append("images", image);
                });
            } else {
                formData.append("images", new Blob([]));
            }

            if (editingProduct.categoryId) {
                formData.append("categoryId", editingProduct.categoryId);
            }

            if (editingProduct.description) {
                formData.append("description", editingProduct.description);
            }

            formData.append("isHotProduct", editingProduct.isHotProduct ? "true" : "false");
            formData.append("isDeleted", editingProduct.isDeleted ? "true" : "false");

            if (editingProduct.imageUrls && editingProduct.imageUrls.length > 0) {
                editingProduct.imageUrls.forEach((url) => {
                    formData.append("existingImageUrls", url);
                });
            }

            if (editingProduct.deletedImageUrls && editingProduct.deletedImageUrls.length > 0) {
                editingProduct.deletedImageUrls.forEach((url) => {
                    formData.append("deletedImageUrls", url);
                });
            }

            console.log("=== UPDATE REQUEST DEBUG ===");
            console.log("Product ID:", editingProduct.id);
            console.log("New image files count:", editingProduct.images?.length || 0);
            console.log("Existing images:", editingProduct.imageUrls);
            console.log("Deleted images:", editingProduct.deletedImageUrls);

            for (let [key, value] of formData.entries()) {
                console.log(`FormData: ${key} =`, value);
            }

            const response = await axios.put(
                `${apiBaseUrl}/manager/product/${editingProduct.id}/update`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            console.log("Update response:", response.data);

            await fetchProducts();
            setShowEditModal(false);
            setEditingProduct(null);

            Notify.success(t("products.success.updated"));
        } catch (error) {
            console.error("Error updating product:", error);
            console.error("Error response:", error.response?.data);
            Notify.failure(t("products.errors.updateFailed") + ": " + (error.response?.data || error.message));
        }
    };

    const handleDetailsClick = (product) => {
        setDetailsProduct(product);
        setShowDetailsModal(true);
    };

    const renderCategoryOptions = () => {
        return categories.map((category) => (
            <option key={category.id} value={category.id}>
                {category.name}
            </option>
        ));
    };

    return (
        <div className="mt-4">
            <h1 className="mb-4">{t('products.title')}</h1>

            <div className="card mb-4">
                <div className="card-body">
                    <h2 className="card-title">{t('products.addNewProduct')}</h2>
                    <form onSubmit={handleAddProduct}>
                        <div className="mb-3">
                            <label htmlFor="category" className="form-label">
                                {t('products.form.category')}
                            </label>
                            <select
                                id="category"
                                className="form-select"
                                value={newProduct.categoryId}
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, categoryId: e.target.value })
                                }
                                required
                            >
                                <option value="">{t('products.form.selectCategory')}</option>
                                {renderCategoryOptions()}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="productName" className="form-label">
                                {t('products.form.productName')}
                            </label>
                            <input
                                id="productName"
                                type="text"
                                className="form-control"
                                value={newProduct.name}
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, name: e.target.value })
                                }
                                placeholder={t('products.form.productNamePlaceholder')}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="productPrice" className="form-label">
                                {t('products.form.price')}
                            </label>
                            <input
                                id="productPrice"
                                type="number"
                                className="form-control"
                                value={newProduct.price}
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, price: e.target.value })
                                }
                                placeholder={t('products.form.pricePlaceholder')}
                                step="0.01"
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="productQuantity" className="form-label">
                                {t('products.form.quantity')}
                            </label>
                            <input
                                id="productQuantity"
                                type="number"
                                className="form-control"
                                value={newProduct.quantity}
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, quantity: e.target.value })
                                }
                                placeholder={t('products.form.quantityPlaceholder')}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="productDescription" className="form-label">
                                {t('products.form.description')}
                            </label>
                            <textarea
                                id="productDescription"
                                className="form-control"
                                value={newProduct.description}
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, description: e.target.value })
                                }
                                placeholder={t('products.form.descriptionPlaceholder')}
                            ></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{t('products.form.specificParams')}</label>
                            {specificParams.map((param, index) => (
                                <div key={index} className="d-flex mb-2">
                                    <input
                                        type="text"
                                        className="form-control me-2"
                                        value={param.key}
                                        onChange={(e) =>
                                            handleSpecificParamChange(index, "key", e.target.value)
                                        }
                                        placeholder={t('products.form.keyPlaceholder')}
                                    />
                                    <input
                                        type="text"
                                        className="form-control me-2"
                                        value={param.value}
                                        onChange={(e) =>
                                            handleSpecificParamChange(index, "value", e.target.value)
                                        }
                                        placeholder={t('products.form.valuePlaceholder')}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        style={{ backgroundColor: "red", border: "red" }}
                                        onClick={() => removeSpecificParam(index)}
                                    >
                                        {t('actions.delete')}
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ backgroundColor: "#0056B3" }}
                                onClick={addSpecificParam}
                            >
                                {t('products.form.addParam')}
                            </button>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="productImages" className="form-label">
                                {t('products.form.images')}
                            </label>
                            <input
                                id="productImages"
                                type="file"
                                className="form-control"
                                onChange={handleImageChange}
                                multiple
                                accept="image/*"
                            />
                            <div className="mt-2">
                                {newProduct.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={URL.createObjectURL(image)}
                                        alt={`${t('products.form.preview')} ${index + 1}`}
                                        style={{
                                            width: "100px",
                                            height: "100px",
                                            objectFit: "cover",
                                            marginRight: "10px",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="mb-3 form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="isHotProduct"
                                checked={newProduct.isHotProduct}
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, isHotProduct: e.target.checked })
                                }
                            />
                            <label className="form-check-label" htmlFor="isHotProduct">
                                {t('products.form.hotProduct')}
                            </label>
                        </div>
                        <button type="submit" className="btn" style={{ backgroundColor: "#0056B3", color: "white" }}>
                            {t('products.addProductButton')}
                        </button>
                    </form>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <h2 className="card-title">{t('products.productsList')}</h2>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                            <tr>
                                <th>{t('products.table.code')}</th>
                                <th>{t('products.table.name')}</th>
                                <th>{t('products.table.price')}</th>
                                <th>{t('products.table.quantity')}</th>
                                <th>{t('products.table.params')}</th>
                                <th>{t('products.table.image')}</th>
                                <th>{t('products.table.status')}</th>
                                <th>{t('products.table.actions')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {products.map((product) => {
                                const rowStyle = product.isDeleted
                                    ? { backgroundColor: "#f8d7da" }
                                    : {};

                                return (
                                    <tr key={product.id} style={rowStyle}>
                                        <td>{product.individualCode}</td>
                                        <td>{product.name}</td>
                                        <td>{formatPriceKZT(product.price)} ₸</td>
                                        <td>{product.quantity}</td>
                                        <td>{renderSpecificParams(product.specificParams, 1)}</td>
                                        <td>
                                            {imageUrls[product.id] ? (
                                                <img
                                                    src={imageUrls[product.id]}
                                                    alt={t('products.table.firstImage')}
                                                    style={{width: "50px", height: "50px", objectFit: "cover"}}
                                                />
                                            ) : (
                                                <span>{t('products.table.noImage')}</span>
                                            )}
                                        </td>
                                        <td>
                                            {product.isDeleted ? (
                                                <span className="badge bg-danger">{t('products.status.deleted')}</span>
                                            ) : (
                                                <span className="badge bg-success">{t('products.status.active')}</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn"
                                                    style={{
                                                        backgroundColor: "#0056B3",
                                                        color: "#fff",
                                                        borderColor: "#0056B3",
                                                    }}
                                                    onClick={() => handleEditClick(product)}
                                                >
                                                    {t('actions.edit')}
                                                </button>
                                                <button
                                                    className="btn"
                                                    style={{
                                                        backgroundColor: "#17a2b8",
                                                        color: "#fff",
                                                        borderColor: "#17a2b8",
                                                    }}
                                                    onClick={() => handleDetailsClick(product)}
                                                >
                                                    {t('actions.details')}
                                                </button>
                                                <button
                                                    className="btn"
                                                    style={
                                                        product.isDeleted
                                                            ? {
                                                                backgroundColor: "gray",
                                                                color: "#fff",
                                                                borderColor: "gray",
                                                                cursor: "not-allowed",
                                                            }
                                                            : {
                                                                backgroundColor: "red",
                                                                color: "#fff",
                                                                borderColor: "red"
                                                            }
                                                    }
                                                    onClick={() => !product.isDeleted && handleDeleteProduct(product.id)}
                                                    disabled={product.isDeleted}
                                                >
                                                    {t('actions.delete')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showEditModal && editingProduct && (
                <div className="modal" tabIndex="-1" role="dialog" style={{display: "block", backgroundColor: "rgba(0,0,0,0.5)"}}>
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('products.modal.editTitle')}</h5>
                                <button type="button" className="btn-close"
                                        onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleUpdateProduct}>
                                    <div className="mb-3">
                                        <label htmlFor="editCategory" className="form-label">
                                            {t('products.form.category')}
                                        </label>
                                        <select
                                            id="editCategory"
                                            className="form-select"
                                            value={editingProduct.categoryId}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    categoryId: e.target.value,
                                                })
                                            }
                                        >
                                            <option value="">{t('products.form.selectCategory')}</option>
                                            {renderCategoryOptions()}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editProductName" className="form-label">
                                            {t('products.form.productName')}
                                        </label>
                                        <input
                                            id="editProductName"
                                            type="text"
                                            className="form-control"
                                            value={editingProduct.name}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    name: e.target.value,
                                                })
                                            }
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editProductPrice" className="form-label">
                                            {t('products.form.price')}
                                        </label>
                                        <input
                                            id="editProductPrice"
                                            type="number"
                                            className="form-control"
                                            value={editingProduct.price}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    price: e.target.value,
                                                })
                                            }
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editProductQuantity" className="form-label">
                                            {t('products.form.quantity')}
                                        </label>
                                        <input
                                            id="editProductQuantity"
                                            type="number"
                                            className="form-control"
                                            value={editingProduct.quantity}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    quantity: e.target.value,
                                                })
                                            }
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editProductDescription" className="form-label">
                                            {t('products.form.description')}
                                        </label>
                                        <textarea
                                            id="editProductDescription"
                                            className="form-control"
                                            value={editingProduct.description || ""}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    description: e.target.value,
                                                })
                                            }
                                        ></textarea>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editProductStatus" className="form-label">
                                            {t('products.form.status')}
                                        </label>
                                        <select
                                            id="editProductStatus"
                                            className="form-select"
                                            value={editingProduct.isDeleted ? "deleted" : "active"}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    isDeleted: e.target.value === "deleted",
                                                })
                                            }
                                        >
                                            <option value="active">{t('products.status.active')}</option>
                                            <option value="deleted">{t('products.status.deleted')}</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">{t('products.form.specificParams')}</label>
                                        {editingProduct.specificParams.map((param, index) => (
                                            <div key={index} className="d-flex mb-2">
                                                <input
                                                    type="text"
                                                    className="form-control me-2"
                                                    value={param.key}
                                                    onChange={(e) => handleEditSpecificParamChange(index, "key", e.target.value)}
                                                    placeholder={t('products.form.keyPlaceholder')}
                                                />
                                                <input
                                                    type="text"
                                                    className="form-control me-2"
                                                    value={param.value}
                                                    onChange={(e) => handleEditSpecificParamChange(index, "value", e.target.value)}
                                                    placeholder={t('products.form.valuePlaceholder')}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-danger"
                                                    onClick={() => removeEditSpecificParam(index)}
                                                    style={{backgroundColor: "red", border: "red"}}
                                                >
                                                    {t('actions.delete')}
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            style={{backgroundColor: "#0056B3"}}
                                            onClick={addEditSpecificParam}
                                        >
                                            {t('products.form.addParam')}
                                        </button>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editProductImages" className="form-label">
                                            {t('products.modal.currentImages')}
                                        </label>
                                        {editingProduct.imageUrls && editingProduct.imageUrls.length > 0 ? (
                                            <div style={{ display: "flex", flexWrap: "wrap" }}>
                                                {editingProduct.imageUrls.map((imageUrl, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            position: "relative",
                                                            marginRight: "10px",
                                                            marginBottom: "10px",
                                                        }}
                                                    >
                                                        <img
                                                            src={`${apiBaseUrl}/public/images/${imageUrl}`}
                                                            alt={`${t('products.modal.image')} ${index + 1}`}
                                                            style={{
                                                                width: "100px",
                                                                height: "100px",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            style={{
                                                                position: "absolute",
                                                                top: 0,
                                                                right: 0,
                                                                backgroundColor: "red",
                                                                border: "none",
                                                                color: "white",
                                                                borderRadius: "50%",
                                                                width: "20px",
                                                                height: "20px",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() => handleDeleteCurrentImage(imageUrl, index)}
                                                        >
                                                            X
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <span>{t('products.modal.noImages')}</span>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="editProductImagesNew" className="form-label">
                                            {t('products.modal.addNewImages')}
                                        </label>
                                        <input
                                            id="editProductImagesNew"
                                            type="file"
                                            className="form-control"
                                            onChange={handleEditImageChange}
                                            multiple
                                            accept="image/*"
                                        />
                                        <div className="mt-2">
                                            {editingProduct.images.map((image, index) => (
                                                <img
                                                    key={index}
                                                    src={URL.createObjectURL(image) || "/placeholder.svg"}
                                                    alt={`${t('products.form.preview')} ${index + 1}`}
                                                    style={{
                                                        width: "100px",
                                                        height: "100px",
                                                        objectFit: "cover",
                                                        marginRight: "10px",
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="editIsHotProduct"
                                            checked={editingProduct.isHotProduct}
                                            onChange={(e) =>
                                                setEditingProduct({
                                                    ...editingProduct,
                                                    isHotProduct: e.target.checked,
                                                })
                                            }
                                        />
                                        <label className="form-check-label" htmlFor="editIsHotProduct">
                                            {t('products.form.hotProduct')}
                                        </label>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary"
                                                onClick={() => setShowEditModal(false)}>
                                            {t('actions.cancel')}
                                        </button>
                                        <button type="submit" className="btn btn-primary">
                                            {t('actions.save')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Modal
                show={showDetailsModal && detailsProduct}
                onHide={() => setShowDetailsModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('products.modal.detailsTitle')}</Modal.Title>
                </Modal.Header>
                {detailsProduct && (
                    <Modal.Body>
                        <div className="row">
                            <div className="col-md-5">
                                {detailsProduct.imageUrls && detailsProduct.imageUrls.length > 0 ? (
                                    <div className="d-flex flex-wrap">
                                        {detailsProduct.imageUrls.map((img, index) => (
                                            <div key={index} className="m-1">
                                                <img
                                                    src={`${apiBaseUrl}/public/images/${img}`}
                                                    alt={`${t('products.modal.image')} ${index + 1}`}
                                                    style={{
                                                        width: "100%",
                                                        maxWidth: "180px",
                                                        height: "auto",
                                                        objectFit: "cover",
                                                        borderRadius: "5px",
                                                        border: "1px solid #ccc",
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted">{t('products.modal.noImages')}</p>
                                )}
                            </div>

                            <div className="col-md-7 mt-3 mt-md-0">
                                <ListGroup variant="flush">
                                    <ListGroup.Item>
                                        <strong>{t('products.table.code')}:</strong> {detailsProduct.individualCode || "—"}
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>{t('products.table.name')}:</strong> {detailsProduct.name}
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>{t('products.form.category')}:</strong>{" "}
                                        {detailsProduct.categoryName ||
                                            (categories.find(cat => cat.id === detailsProduct.categoryId)?.name || "—")}
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>{t('products.table.price')}:</strong> {formatPriceKZT(detailsProduct.price)} ₸
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>{t('products.table.quantity')}:</strong> {detailsProduct.quantity}
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>{t('products.table.status')}:</strong>{" "}
                                        {detailsProduct.isDeleted ? t('products.status.deleted') : t('products.status.active')}
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <strong>{t('products.form.specificParams')}:</strong>
                                        {renderSpecificParams(detailsProduct.specificParams)}
                                    </ListGroup.Item>
                                </ListGroup>
                            </div>
                        </div>
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        {t('products.modal.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Products;
